// Shared composer types. Every piece of the post draft lives in a single
// `ComposerDraft` object so the whole thing can be submitted to the API at once.

export type LinkKind = "youtube" | "vimeo" | "link";

// "loading": eager fetch in flight, render a skeleton. "resolved": real
// metadata in hand, render the rich card. "bare": fetch failed, timed out,
// or (on a published post) never produced anything — render a bare chip.
// Rendering always keys off this, never off whether title/image happen to
// be present.
export type LinkAttachmentStatus = "loading" | "resolved" | "bare";

export interface LinkAttachment {
  // Local id used only for list rendering / removal.
  id: string;
  kind: LinkKind;
  url: string;
  domain: string;
  // Video id (YouTube / Vimeo) when kind is a video.
  videoId?: string;
  status: LinkAttachmentStatus;
  // Real metadata from the server unfurl — only meaningful when
  // status === "resolved".
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
  // Readable site label for the bare-chip state.
  site?: string;
}

export interface PhotoAttachment {
  id: string;
  // The raw file is what eventually goes to S3 as multipart/form-data.
  file: File;
  // Object URL used purely for the local preview thumbnail (never uploaded).
  previewUrl: string;
  alt: string;
}

// A normalized image attachment for rendering — the displayable src/alt with
// no upload-only fields. Both a composer preview (object URL) and a posted
// card (stored S3 url) reduce to this for AttachmentCard.
export interface ImageAttachment {
  id: string;
  src: string;
  alt: string;
}

export const toImageAttachment = (photo: PhotoAttachment): ImageAttachment => ({
  id: photo.id,
  src: photo.previewUrl,
  alt: photo.alt,
});

// Attachments only. The editor's text lives in the contenteditable DOM (read
// on submit via the editor handle), not here — mirroring it into React state
// on every keystroke was redundant.
export interface ComposerDraft {
  photos: PhotoAttachment[];
  links: LinkAttachment[];
}

export const emptyDraft = (): ComposerDraft => ({
  photos: [],
  links: [],
});

// A draft has nothing to post when the editor is empty and there are no
// attachments. Editor emptiness is tracked separately (see Composer).
export const hasNothingToPost = (
  draft: ComposerDraft,
  isEditorEmpty: boolean,
  hasKeptExistingImage = false,
): boolean =>
  isEditorEmpty &&
  draft.photos.length === 0 &&
  draft.links.length === 0 &&
  !hasKeptExistingImage;
